/**
 * React Hook for Real-Time Data
 * 
 * Provides easy access to real-time data from the Agrotrack API
 * with automatic loading states and error handling.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import realTimeDataService from '../services/realTimeDataService';

/**
 * Generic hook for fetching data with loading and error states
 */
export function useDataFetch(fetchFn, deps = [], options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const { autoRefresh = false, refreshInterval = 60000, enabled = true } = options;
  const mountedRef = useRef(true);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFn();
      if (mountedRef.current) {
        setData(result);
        setLastUpdated(new Date());
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || 'Failed to fetch data');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [fetchFn, enabled]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    if (autoRefresh && refreshInterval > 0) {
      intervalRef.current = setInterval(fetchData, refreshInterval);
    }

    return () => {
      mountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, autoRefresh, refreshInterval]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, lastUpdated, refetch };
}

/**
 * Hook for API status
 */
export function useAPIStatus() {
  const fetchFn = useCallback(() => realTimeDataService.getAPIStatus(), []);
  return useDataFetch(fetchFn, [], { autoRefresh: false });
}

/**
 * Hook for weather data
 */
export function useWeatherData(lat, lon, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getWeather(lat, lon),
    [lat, lon]
  );
  return useDataFetch(fetchFn, [lat, lon], {
    autoRefresh: true,
    refreshInterval: 300000, // 5 minutes
    ...options
  });
}

/**
 * Hook for agricultural weather metrics
 */
export function useAgriculturalWeather(lat, lon, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getAgriculturalWeather(lat, lon),
    [lat, lon]
  );
  return useDataFetch(fetchFn, [lat, lon], {
    autoRefresh: true,
    refreshInterval: 300000,
    ...options
  });
}

/**
 * Hook for crop statistics
 */
export function useCropStats(state, year, crop, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getCropStats(state, year, crop),
    [state, year, crop]
  );
  return useDataFetch(fetchFn, [state, year, crop], options);
}

/**
 * Hook for crop distribution
 */
export function useCropDistribution(state, year, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getCropDistribution(state, year),
    [state, year]
  );
  return useDataFetch(fetchFn, [state, year], options);
}

/**
 * Hook for crop health
 */
export function useCropHealth(state, crop, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getCropHealth(state, crop),
    [state, crop]
  );
  return useDataFetch(fetchFn, [state, crop], options);
}

/**
 * Hook for crop yields
 */
export function useCropYields(state, crop, years, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getCropYields(state, crop, years),
    [state, crop, years]
  );
  return useDataFetch(fetchFn, [state, crop, years], options);
}

/**
 * Hook for NDVI data
 */
export function useNDVIData(lat, lon, radiusKm, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getNDVIData(lat, lon, radiusKm),
    [lat, lon, radiusKm]
  );
  return useDataFetch(fetchFn, [lat, lon, radiusKm], {
    autoRefresh: true,
    refreshInterval: 600000, // 10 minutes
    ...options
  });
}

/**
 * Hook for NDVI time series
 */
export function useNDVITimeSeries(lat, lon, days, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getNDVITimeSeries(lat, lon, days),
    [lat, lon, days]
  );
  return useDataFetch(fetchFn, [lat, lon, days], options);
}

/**
 * Hook for vegetation changes
 */
export function useVegetationChanges(lat, lon, threshold, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getVegetationChanges(lat, lon, threshold),
    [lat, lon, threshold]
  );
  return useDataFetch(fetchFn, [lat, lon, threshold], options);
}

/**
 * Hook for biodiversity metrics
 */
export function useBiodiversityMetrics(lat, lon, radiusKm, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getBiodiversityMetrics(lat, lon, radiusKm),
    [lat, lon, radiusKm]
  );
  return useDataFetch(fetchFn, [lat, lon, radiusKm], options);
}

/**
 * Hook for pollinator data
 */
export function usePollinatorData(lat, lon, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getPollinatorData(lat, lon),
    [lat, lon]
  );
  return useDataFetch(fetchFn, [lat, lon], options);
}

/**
 * Hook for biodiversity trends
 */
export function useBiodiversityTrends(lat, lon, years, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getBiodiversityTrends(lat, lon, years),
    [lat, lon, years]
  );
  return useDataFetch(fetchFn, [lat, lon, years], options);
}

/**
 * Hook for pest predictions
 */
export function usePestPredictions(lat, lon, crop, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getPestPredictions(lat, lon, crop),
    [lat, lon, crop]
  );
  return useDataFetch(fetchFn, [lat, lon, crop], {
    autoRefresh: true,
    refreshInterval: 600000, // 10 minutes
    ...options
  });
}

/**
 * Hook for pest history
 */
export function usePestHistory(lat, lon, pest, years, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getPestHistory(lat, lon, pest, years),
    [lat, lon, pest, years]
  );
  return useDataFetch(fetchFn, [lat, lon, pest, years], options);
}

/**
 * Hook for IPM recommendations
 */
export function useIPMRecommendations(crop, growthStage, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getIPMRecommendations(crop, growthStage),
    [crop, growthStage]
  );
  return useDataFetch(fetchFn, [crop, growthStage], options);
}

/**
 * Hook for ML models
 */
export function useModels(options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getModels(),
    []
  );
  return useDataFetch(fetchFn, [], options);
}

/**
 * Hook for complete dashboard data
 */
export function useDashboardData(lat, lon, state, options = {}) {
  const fetchFn = useCallback(
    () => realTimeDataService.getDashboardData(lat, lon, state),
    [lat, lon, state]
  );
  return useDataFetch(fetchFn, [lat, lon, state], {
    autoRefresh: true,
    refreshInterval: 300000, // 5 minutes
    ...options
  });
}

/**
 * Hook for yield prediction
 */
export function useYieldPrediction(features, modelName, options = {}) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const predict = useCallback(async (featuresToPredict = features) => {
    if (!featuresToPredict) return;

    setLoading(true);
    setError(null);

    try {
      const result = await realTimeDataService.getYieldPrediction(featuresToPredict, modelName);
      setPrediction(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [features, modelName]);

  return { prediction, loading, error, predict };
}

const realTimeDataHooks = {
  useDataFetch,
  useAPIStatus,
  useWeatherData,
  useAgriculturalWeather,
  useCropStats,
  useCropDistribution,
  useCropHealth,
  useCropYields,
  useNDVIData,
  useNDVITimeSeries,
  useVegetationChanges,
  useBiodiversityMetrics,
  usePollinatorData,
  useBiodiversityTrends,
  usePestPredictions,
  usePestHistory,
  useIPMRecommendations,
  useModels,
  useDashboardData,
  useYieldPrediction
};

export default realTimeDataHooks;
